import { resetUserProfile } from "../reducers/userSlice";
import { LOCAL_STORAGE_KEY } from "./constant";
import moment from "moment";
import { showAlert } from "../reducers/alertSlice";
import { clearPatient } from "../reducers/patientSlice";
import { Box, Typography } from "@mui/material";

export const isDev = () => {
    if (["DEV", "STAGE"].includes(process.env.REACT_APP_ENV)) {
        return true;
    }

    return false;
};

export const isRoot = (location) => {
    return location.pathname === "/";
};

export const hasAccess = (user, permissionConfig, type, permission) => {
    if (!user || !user.role || !permissionConfig) {
        return false;
    }

    if (!permissionConfig.rolesPermissions) {
        return false;
    }

    if (!permissionConfig.data || !permissionConfig.data[type]) {
        return false;
    }

    const data = permissionConfig.data[type];

    const rolesPermissions = permissionConfig.rolesPermissions;

    const myPermissions = rolesPermissions[user.role];

    //if the role is not defined, return false
    if (!myPermissions) {
        return false;
    }

    //if the role has superAdmin, return true
    if (myPermissions.superAdmin) {
        return true;
    }

    //if the data is not defined, return false
    if (!myPermissions[data] || !myPermissions[data].includes(permission)) {
        return false;
    }

    return true;
};

export const isSubstringInArray = (str, list) => {
    if (!str || !Array.isArray(list)) {
        return false;
    }

    return list.some((item) => str.includes(item));
};

export const printLog = (item, error = false) => {
    if (!isDev()) {
        return;
    }

    if (!error) {
        console.log(item);
        return;
    }

    console.error(item);
};

// Table filters go in the query string of a GET. Sent indexed
// (extraParams[buildVersion][0]=...) each value costs ~45 bytes, and AWS WAF in
// front of the API blocks query strings over 2048 bytes — selecting most app
// versions tripped it. Join each list into one comma-separated value instead;
// the backend splits it. Empty lists are dropped, as before.
export const compactListParams = (extraParams) => {
    if (!extraParams) {
        return extraParams;
    }

    const compact = {};
    Object.entries(extraParams).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            if (value.length > 0) {
                compact[key] = value.join(",");
            }
        } else if (value !== null && value !== undefined) {
            compact[key] = value;
        }
    });

    return compact;
};

export const httpRequest = async (
    endpoint,
    data,
    method = "GET",
    errorCompletion,
) => {
    try {
        let url = `${process.env.REACT_APP_ENDPOINT}/${endpoint}`;

        const headers = {
            Authorization: `Bearer ${getStorage(LOCAL_STORAGE_KEY.token)}`,
        };

        const config = {
            method: method,
            credentials: "include",
            headers: headers,
        };
        if (method === "GET") {
            if (data && Object.keys(data).length > 0) {
                const parts = [];
                const serializeParam = (key, value) => {
                    if (Array.isArray(value)) {
                        value.forEach((v, i) =>
                            serializeParam(`${key}[${i}]`, v),
                        );
                    } else if (
                        value !== null &&
                        value !== undefined &&
                        typeof value === "object"
                    ) {
                        Object.entries(value).forEach(([k, v]) =>
                            serializeParam(`${key}[${k}]`, v),
                        );
                    } else if (value !== null && value !== undefined) {
                        parts.push(
                            `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
                        );
                    }
                };
                Object.entries(data).forEach(([k, v]) => serializeParam(k, v));
                url = `${url}?${parts.join("&")}`;
            }
        } else {
            headers["Content-Type"] = "application/json";
            config.body = JSON.stringify(data);
        }

        const response = await fetch(url, config);

        if (response.ok) {
            return await response.json();
        }

        if (!errorCompletion) {
            return false;
        }

        let error = {
            title: "Error",
            message: "Something went wrong with your request",
        };
        if (response.status !== 404) {
            try {
                const errorData = await response.json();
                if (errorData && errorData.error) {
                    error = errorData.error;
                }
            } catch (_) {}
        }

        errorCompletion(error);
        return false;
    } catch (e) {
        console.log("***** ERROR", e);
        if (!errorCompletion) {
            return false;
        }

        errorCompletion({ title: "Error", message: e.message });
        return false;
    }
};

export const httpUpload = async (
    endpoint,
    data,
    method = "POST",
    errorCompletion,
) => {
    try {
        let url = `${process.env.REACT_APP_ENDPOINT}/${endpoint}`;

        // Don't set Content-Type — browser sets it automatically with the multipart boundary
        const headers = {
            Authorization: `Bearer ${getStorage(LOCAL_STORAGE_KEY.token)}`,
        };

        const config = {
            method: method,
            credentials: "include",
            headers: headers,
        };

        if (method === "GET") {
            if (data && Object.keys(data).length > 0) {
                const params = new URLSearchParams(data);
                url = `${url}?${params.toString()}`;
            }
        } else {
            config.body = data; // expects a FormData object
        }

        const response = await fetch(url, config);

        if (response.ok) {
            return await response.json();
        }

        if (!errorCompletion) {
            return false;
        }

        let error = {
            title: "Error",
            message: "Something went wrong with your request",
        };
        if (response.status !== 404) {
            try {
                const errorData = await response.json();
                if (errorData && errorData.error) {
                    error = errorData.error;
                }
            } catch (_) {}
        }

        errorCompletion(error);
        return false;
    } catch (e) {
        console.log("***** ERROR", e);
        if (!errorCompletion) {
            return false;
        }

        errorCompletion({ title: "Error", message: e.message });
        return false;
    }
};

export const onRequestError = (dispatch, navigate, data) => {
    if (!dispatch || !data) {
        return;
    }

    switch (data.title.toLowerCase()) {
        case "login":
            // data.buttons = [
            //     {
            //         label: "Login",
            //         callback: () => {
            //             userLogout(dispatch, navigate);
            //         },
            //     },
            // ];

            userLogout(dispatch, navigate);
            return;
            break;

        default:
            break;
    }

    dispatch(showAlert(data));
};

//user logout, this method will clear data in local storage and redux
export const userLogout = (dispatch, navigate) => {
    deleteStorage(LOCAL_STORAGE_KEY.token);
    // deleteStorage(LOCAL_STORAGE_KEY.patientsCache);
    // deleteStorage(LOCAL_STORAGE_KEY.usersCache);
    dispatch(resetUserProfile());
    dispatch(clearPatient());

    navigate("/");
};

// Function to set an item in localStorage
export const setStorage = (key, value) => {
    try {
        localStorage.setItem(key, value);
    } catch (error) {
        console.error(`Error setting localStorage item ${key}:`, error);
    }
};

// Function to get an item from localStorage
export const getStorage = (key, json = false) => {
    try {
        const item = localStorage.getItem(key);

        if (!json) {
            return item ?? null;
        }

        return JSON.parse(item) ?? null;
    } catch (error) {
        console.error(`Error getting localStorage item ${key}:`, error);
        return null;
    }
};

// Function to delete an item from localStorage
export const deleteStorage = (key) => {
    try {
        localStorage.removeItem(key);
    } catch (error) {
        console.error(`Error deleting localStorage item ${key}:`, error);
    }
};

export const DATE_FORMAT = {
    defaultDate: "YYYY-MM-DD",
    defaultTime: "HH:mm:ss",
};

export const formatDateTime = (
    date,
    format = `${DATE_FORMAT.defaultDate} ${DATE_FORMAT.defaultTime}`,
    utc = true,
) => {
    if (!date) {
        return "";
    }

    if (utc) {
        return moment(date).utc().format(format);
    }

    return moment(date).format(format);
};

export const computeBMI = (weight, height) => {
    if (!weight || !height) {
        return 0;
    }
    const bmi = (weight / Math.pow(height, 2)) * 703;

    return bmi.toFixed(2);
};

export const CONVERT = {
    inToFtIn: (inches) => {
        if (!inches || isNaN(inches)) {
            return { feet: "", inch: "" };
        }

        const totalInches = Math.round(inches);
        const feet = Math.floor(totalInches / 12);
        const remainingInches = totalInches % 12;
        return { feet, inch: remainingInches };
    },

    ftinToIn: (feet, inches) => {
        if (!feet || !inches || isNaN(feet) || isNaN(inches)) {
            return 0;
        }

        feet = feet * 1;
        inches = inches * 1;

        return feet * 12 + inches;
    },
};

export const buildTableDateFormatter = (theme, value) => {
    const [date, time] = formatDateTime(
        value,
        "MMM D, YYYY|h:mm a",
        false,
    ).split("|");
    return (
        <Box>
            <Typography variant="body" sx={{ whiteSpace: "nowrap" }}>
                {date}
            </Typography>
            <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
            >
                {time}
            </Typography>
        </Box>
    );
};

export const generateCSV = (column, data, filename = "export.csv") => {
    if (!Array.isArray(data) || data.length === 0) {
        console.error("Data must be a non-empty array.");
        return;
    }

    const header = column.join(",");
    const rows = data.map((obj) => obj.join(",")).join("\n");
    const csvContent = `${header}\n${rows}`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    if (navigator.msSaveBlob) {
        // IE 10+
        navigator.msSaveBlob(blob, filename);
    } else {
        const link = document.createElement("a");
        if (link.download !== undefined) {
            // Browsers that support HTML5 download attribute
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", filename);
            link.style.visibility = "hidden";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            // Older browsers
            window.open(
                "data:text/csv;charset=utf-8," + encodeURIComponent(csvContent),
            );
        }
    }
};

export const formatStepCount = (value) => {
    if (!value) {
        return "-";
    }
    return Number(value).toLocaleString();
};

export const formatNumber = (value) => {
    if (!value) {
        return "-";
    }
    return Number(value).toLocaleString();
};

export const getNumberSign = (num) => {
    if (!num) {
        return "";
    }
    return num > 0 ? "+" : "";
};

export const computeBodyFatChange = (change, changeLeanMass) => {
    if (!change || !changeLeanMass) {
        return 0;
    }

    const w = parseFloat(change);
    const l = parseFloat(changeLeanMass);

    if (isNaN(w) || isNaN(l)) {
        return 0;
    }

    let fatChange = w - l;
    fatChange = Number(fatChange.toFixed(2));

    fatChange = `${getNumberSign(fatChange)}${fatChange}`;

    return fatChange;
};

export const formatBodyFatPercentage = (value) => {
    if (value === null || value === undefined) {
        return "-";
    }
    //convert to 100 scale, then decimal 1
    const percentage = (parseFloat(value) * 100).toFixed(1);

    return percentage;
};

//create a method to convert minutes to hours and minutes, return in format of 1h 30m
export const formatMinutesToHours = (minutes) => {
    if (!minutes || isNaN(minutes)) {
        return "-";
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    let result = "";
    if (hours > 0) {
        result += `${hours}h `;
    }
    if (remainingMinutes > 0) {
        result += `${remainingMinutes}m`;
    }

    return result.trim();
};
